import axios from "axios";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useAxiosHook } from "./useAxiosHook";
import { usePipeline } from "../pipelines/PipelineContext";
import { API_SOURCE } from "../pipelines/pipelineConfig";
import { isArray, isEmpty, isNil, size } from "lodash";

const ROUTE_PREFIX = "checklists";
const ACQ_PREFIX = "loan_acquisitions";

// Acquisitions (postclose) keeps its conditions on its OWN router, with
// different path names AND a different response shape: it returns the deal-keyed
// doc `{ _id, conditions: [...] }`, where preclose returns the conditions array.
// Normalize to the array so every existing caller/consumer is unchanged.
const acqConditions = (res) =>
  isArray(res) ? res : isArray(res?.conditions) ? res.conditions : [];

export const useChecklistRouteHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  // Which router this pipeline's conditions / task lists live on. Preclose =
  // /checklists; acquisitions = /loan_acquisitions (see pipelineConfig.js).
  const { capabilities } = usePipeline();
  const useAcqConditions = capabilities?.conditionsApi === API_SOURCE.PIPELINE;

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const checklistRouteLoading = loading;
  const error = axiosError;

  const getChecklistTasksList = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/underwriting/tasklist_templates`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Checklist Task Lists",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createNewTaskList = async ({
    dealId,
    taskListId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    // if (isNil(dealId) || isEmpty(taskListId)) {
    //   console.error("Task list data is missing", { dealId, taskListId });
    //   return;
    // }

    validateRequiredFields({ dealId, taskListId });
    const endpoint = `${ROUTE_PREFIX}/create_uw_tasklist/${dealId}/${taskListId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Loan created successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const getTaskListById = async ({
    dealId,
    taskListId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, taskListId });

    // if (isNil(dealId) || isEmpty(taskListId)) {
    //   console.error("Task list data is missing", { dealId, taskListId });
    //   return;
    // }
    const endpoint = `${ROUTE_PREFIX}/retrieve_uw_tasklist/${dealId}/${taskListId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Task List By ID",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateTaskData = async ({
    dealId,
    taskListId,
    taskId,
    data,
    onSuccessFn,
    onSuccessMsg = "Task updated successfully",
    onCompleteFn,
    onFailFn,
  }) => {
    // if (isNil(dealId) || isNil(data) || isEmpty(data)) {
    //   console.error("Data is missing", { dealId, data });
    //   return;
    // }
    validateRequiredFields({ dealId, taskListId, taskId, data });
    const endpoint = `${ROUTE_PREFIX}/update_task/${dealId}/${taskListId}/${taskId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Task Data",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const bulkUpdateTaskData = async ({
    dealId,
    taskListId,
    data,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, taskListId, data });
    const endpoint = `${ROUTE_PREFIX}/bulk_update_tasks/${dealId}/${taskListId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Bulk Update Checklist Tasks",
      path: endpoint,
      payload: data, // typically an array of JSON objects
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: `Updated ${size(data)} Checklist Tasks`,
      onFailFn,
    });
  };

  const createChecklistComment = async ({
    dealId,
    taskListId,
    taskId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(taskListId) || isNil(taskId) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { dealId, taskListId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_task_comment/${dealId}/${taskListId}/${taskId}`;
    const source = axios.CancelToken.source();

    // console.log("createChecklistComment", data);
    // return;
    await axiosPostRequest({
      name: "Create Checklist Task Comment",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: testing,
      onCompleteFn,
      onFailFn,
    });
  };

  const createChecklistCommentWithFiles = async ({
    dealId,
    taskListId,
    taskId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (
      isNil(dealId) ||
      isNil(taskListId) ||
      isNil(taskId) ||
      isNil(data) ||
      isEmpty(data)
    ) {
      console.error("Loan ID or Data is missing", { dealId, data });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/create_task_comment_with_uploads/${dealId}/${taskListId}/${taskId}`;
    const source = axios.CancelToken.source();
    // console.log("createChecklistCommentWithFiles", {
    //   dealId,
    //   taskListId,
    //   data,
    // });
    // // return;
    await axiosPostRequest({
      name: "Create Deal Comment",
      path: endpoint,
      payload: data,
      formData:
        isArray(data?.uploadMetadata) && !isEmpty(data?.uploadMetadata)
          ? true
          : false,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createChecklistTaskException = async ({
    dealId,

    data,
    onSuccessFn,
    onSuccessMsg = "Exception Request Submitted",
    onCompleteFn,
    onFailFn,
  }) => {
    // return;
    validateRequiredFields({ dealId, data });
    const endpoint = `${ROUTE_PREFIX}/create_task_exception_request/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Checklist Task Exception",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const createChecklistHardException = async ({
    dealId,
    data,
    onSuccessFn,
    onSuccessMsg = "Exception Request Submitted",
    onCompleteFn,
    onFailFn,
  }) => {
    // return;
    validateRequiredFields({ dealId, data });
    const endpoint = `${ROUTE_PREFIX}/create_hard_exception_request/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Checklist Hard Exception",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateChecklistException = async ({
    dealId,
    exceptionId,
    data,
    onSuccessFn,
    onSuccessMsg = "Exception Updated",
    onCompleteFn,
    onFailFn,
  }) => {
    // return;
    validateRequiredFields({ dealId, data, exceptionId });
    const endpoint = `${ROUTE_PREFIX}/update_exception_request/${dealId}/${exceptionId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Exception",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  // Unified create endpoint — branches on `isHardException` to build either a
  // HardException or a plain LoanException. Supersedes create_hard_exception_request.
  // Accepts `addToMainComments` to atomically mirror an initial comment into
  // deal.comments[]. See EXCEPTION_RECENT_CHANGES_AND_DELETE_GUIDE.md §2.
  const createLoanExceptionRequest = async ({
    dealId,
    data,
    onSuccessFn,
    onSuccessMsg = "Exception Request Submitted",
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, data });
    // /checklists is hardcoded to the underwriting_pipeline collection, so an
    // acquisition deal posted there comes back "Deal {dealId} not found".
    // Acquisitions pushes onto the same `exceptions[]` array through its own
    // router. Its handler builds a bare LoanException and has no
    // addToMainComments branch, so that flag is dropped rather than stored as a
    // stray field on the exception.
    if (useAcqConditions) {
      const { addToMainComments, ...acqPayload } = data || {};
      await axiosPostRequest({
        name: "Create Acquisition Loan Exception",
        path: `${ACQ_PREFIX}/create_loan_exception/${dealId}`,
        payload: acqPayload,
        cancelSource: axios.CancelToken.source(),
        onSuccessFn,
        onSuccessMsg,
        onCompleteFn,
        onFailFn,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_exception_request/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan Exception Request",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  // Hard delete (no archive/restore). POST, no body, returns the refreshed deal.
  // See EXCEPTION_RECENT_CHANGES_AND_DELETE_GUIDE.md §3.
  const deleteChecklistException = async ({
    dealId,
    exceptionId,
    onSuccessFn,
    onSuccessMsg = "Exception deleted",
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, exceptionId });
    const endpoint = `${ROUTE_PREFIX}/delete_exception_request/${dealId}/${exceptionId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Loan Exception",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const getLoanUnderwritingRequirements = async ({
    dealId,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
    });

    // Acquisitions: GET /loan_acquisitions/conditions/{dealId} → { conditions }
    const endpoint = useAcqConditions
      ? `${ACQ_PREFIX}/conditions/${dealId}`
      : `${ROUTE_PREFIX}/retrieve_underwriting_requirements/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: `Get Loan Underwriting Requirements`,
      path: endpoint,
      cancelSource: source,
      onSuccessFn: useAcqConditions
        ? (res) => onSuccessFn?.(acqConditions(res))
        : onSuccessFn,
      onFailFn,
    });
  };

  // GET /checklists/check_missing_documents/{dealId}
  // Returns the loan's outstanding document requirements (checklist-derived):
  // `{ dealId, ..., missing_documents: [...], outstanding_count }`. Each
  // missing item is a requirement (docGroup/docType/description/status/counts),
  // NOT a stored loan document — note it currently keys on `oid`, not `_id`.
  const getMissingDocuments = async ({ dealId, onSuccessFn, onFailFn }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/check_missing_documents/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Check Missing Documents",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const createLoanUnderwritingRequirement = async ({
    dealId,
    data,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      data,
    });
    // Acquisitions: POST /loan_acquisitions/create_condition/{dealId}. The
    // backend fills _id / status:'open' / createdBy / createDateTime itself.
    const endpoint = useAcqConditions
      ? `${ACQ_PREFIX}/create_condition/${dealId}`
      : `${ROUTE_PREFIX}/create_underwriting_requirement/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Create Underwriting Requirement`,
      path: endpoint,
      payload: data,

      cancelSource: source,
      onSuccessFn: useAcqConditions
        ? (res) => onSuccessFn?.(acqConditions(res))
        : onSuccessFn,
      onFailFn,
    });
  };

  const createLoanUnderwritingRequirementWithFile = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      data,
    });
    const hasUpload = isArray(data?.uploadMetadata) && !isEmpty(data?.uploadMetadata);

    // The acquisitions router has no create-condition-with-upload endpoint. With
    // no file attached this is just a create, so fall through to that; with a
    // file, fail loudly rather than silently dropping the upload.
    if (useAcqConditions) {
      if (hasUpload) {
        onFailFn?.(
          new Error(
            "Attaching a file while creating a condition isn't supported on acquisitions loans. Create the condition, then upload the document against it."
          )
        );
        onCompleteFn?.();
        return;
      }
      await createLoanUnderwritingRequirement({ dealId, data, onSuccessFn, onFailFn });
      onCompleteFn?.();
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/create_underwriting_requirement_with_upload/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Create Underwriting Requirement With File`,
      path: endpoint,
      payload: data,
      formData: hasUpload,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanUnderwritingRequirement = async ({
    dealId,
    requirementId,
    data,
    onSuccessFn,
    onFailFn,
    onSuccessMsg = "Underwriting Requirement Updated",
  }) => {
    validateRequiredFields({
      dealId,
      requirementId,
      data,
    });
    // Acquisitions: POST /loan_acquisitions/update_condition/{dealId}/{conditionId}
    const endpoint = useAcqConditions
      ? `${ACQ_PREFIX}/update_condition/${dealId}/${requirementId}`
      : `${ROUTE_PREFIX}/update_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Update Underwriting Requirement`,
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn: useAcqConditions
        ? (res) => onSuccessFn?.(acqConditions(res))
        : onSuccessFn,
      onSuccessMsg,
      onFailFn,
    });
  };

  /**
   * Soft-delete an underwriting requirement (loan condition). The backend
   * archives the record (still recoverable via the restore endpoint) and
   * returns the remaining active conditions.
   *
   * Response shape: { deletedRequirementId, archivedId, conditions }
   * Hold onto `archivedId` if you want to offer undo.
   */
  const deleteLoanUnderwritingRequirement = async ({
    dealId,
    requirementId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, requirementId });

    // No delete_condition endpoint exists on the acquisitions router. Consumers
    // should hide the affordance via `canDeleteCondition`; this is the backstop.
    if (useAcqConditions) {
      onFailFn?.(
        new Error("Deleting a condition isn't supported on acquisitions loans.")
      );
      onCompleteFn?.();
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/delete_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Delete Underwriting Requirement`,
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Condition deleted",
      onCompleteFn,
      onFailFn,
    });
  };

  /**
   * List archived (soft-deleted) underwriting requirements for a deal,
   * most recent first. Each record carries `originalRequirementId` (the _id
   * it had on the deal) and a fresh `_id` to use as `archivedId` when
   * calling the restore endpoint.
   */
  const getDeletedUnderwritingRequirements = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/deleted_underwriting_requirements/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: `Get Deleted Underwriting Requirements`,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  /**
   * Restore an archived underwriting requirement back onto the deal. The
   * restore keeps the original _id so existing comments / associations
   * continue to resolve correctly.
   *
   * Response shape: { restoredRequirementId, conditions }
   */
  const restoreLoanUnderwritingRequirement = async ({
    dealId,
    archivedId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, archivedId });
    const endpoint = `${ROUTE_PREFIX}/restore_underwriting_requirement/${dealId}/${archivedId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Restore Underwriting Requirement`,
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Condition restored",
      onCompleteFn,
      onFailFn,
    });
  };

  const addCommentToUnderwritingException = async ({
    dealId,
    payload,
    excecptionId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ payload, dealId, excecptionId });
    const endpoint = `${ROUTE_PREFIX}/add_comment_to_exception/${dealId}/${excecptionId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Comment To Underwriting Exception",
      // payload: {
      //   htmlString: commentText,
      // },
      payload,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Comment added successfully",
      onFailFn,
    });
  };

  /**
   * ########## UNDERWRITING REQUIREMENTS ###########
   */
  const addCommentToUnderwritingRequirement = async ({
    commentText,
    dealId,
    requirementId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ commentText, dealId, requirementId });
    const endpoint = `${ROUTE_PREFIX}/add_comment_to_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Comment To Underwriting Requirement",
      payload: {
        htmlString: commentText,
      },
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Comment added successfully",
      onFailFn,
    });
  };

  const uploadFilesToUnderwritingRequirement = async ({
    files,
    dealId,
    requirementId,
    payload,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ files, dealId, requirementId });

    const endpoint = `${ROUTE_PREFIX}/upload_loan_docs_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Upload Documents To UW Requirement",
      path: endpoint,
      files: files,
      formData: true, // For file uploads
      payload,
      cancelSource: source,
      // errorTesting: true,
      onSuccessFn,
      onSuccessMsg:
        size(files) > 1
          ? "Documents uploaded successfully"
          : "Document uploaded successfully",
      onCompleteFn,
      onFailFn,
    });
  };
  return {
    error,
    loading,
    checklistRouteLoading,
    // Capability flags for condition affordances the acquisitions router lacks —
    // use these to hide the buttons rather than letting the call fail.
    canDeleteCondition: !useAcqConditions,
    canCreateConditionWithFile: !useAcqConditions,
    getChecklistTasksList,
    createNewTaskList,
    getTaskListById,
    updateTaskData,
    createChecklistCommentWithFiles,
    createChecklistComment,
    bulkUpdateTaskData,
    createChecklistTaskException,
    createChecklistHardException,
    createLoanExceptionRequest,
    updateChecklistException,
    deleteChecklistException,
    getLoanUnderwritingRequirements,
    getMissingDocuments,
    updateLoanUnderwritingRequirement,
    createLoanUnderwritingRequirement,
    createLoanUnderwritingRequirementWithFile,
    deleteLoanUnderwritingRequirement,
    getDeletedUnderwritingRequirements,
    restoreLoanUnderwritingRequirement,
    uploadFilesToUnderwritingRequirement,
    addCommentToUnderwritingRequirement,
    addCommentToUnderwritingException,
  };
};
